// Layer 3 (Transformations) — geometry for the 24 h control strip that rides a
// loop row and a reading row (locked comp `process.html` A, B's variable read):
// the same control-chart object the selected chart draws, scaled to a row.
// Pure: the y-domain comes from the shared chart's projection.

import { controlChartYDomain, flaggedPointTone } from '@/versions/v1/modules/_viz/ControlChart.transform';
import type { ProcessChart } from './Scene.transform';

export type ControlStripGeometry = {
  width: number;
  height: number;
  sigma?: { y: number; height: number };
  ucl: number;
  lcl: number;
  mean: number;
  path: string;
  /** Engine-flagged points: state-blocked beyond a limit, warning inside. */
  flagged: { x: number; y: number; tone: 'bad' | 'warn' }[];
  /** With nothing flagged, the latest point is dotted so the strip ends somewhere. */
  last?: { x: number; y: number };
  shade?: { x: number; width: number };
};

const PAD = { l: 2, r: 2, t: 3, b: 3 };

export function controlStripGeometry(chart: ProcessChart, width: number, height: number): ControlStripGeometry | undefined {
  const { model } = chart;
  const limits = model.limits;
  const n = model.points.length;
  if (!limits || n < 2) return undefined;
  const domain = controlChartYDomain(model);
  const innerW = width - PAD.l - PAD.r;
  const innerH = height - PAD.t - PAD.b;
  const x = (index: number) => PAD.l + (innerW * index) / (n - 1);
  const y = (value: number) => PAD.t + (innerH * (domain.max - value)) / (domain.max - domain.min);
  const round = (value: number) => Math.round(value * 10) / 10;
  const flagged = model.points
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => point.highlighted)
    .map(({ point, index }) => ({ x: round(x(index)), y: round(y(point.value)), tone: flaggedPointTone(point.value, limits) }));
  const range = model.excursionRange;
  let shade: ControlStripGeometry['shade'];
  if (range) {
    const start = model.points.findIndex((point) => point.at >= range.startAt);
    const end = model.points.reduce((found, point, index) => (point.at <= range.endAt ? index : found), -1);
    if (start >= 0 && end >= start) {
      const x0 = Math.max(PAD.l, x(start) - 3);
      const x1 = Math.min(width - PAD.r, x(end) + 3);
      shade = { x: round(x0), width: round(x1 - x0) };
    }
  }
  const lastPoint = model.points[n - 1]!;
  return {
    width,
    height,
    sigma: limits.sigma && limits.sigma > 0
      ? { y: round(y(limits.mean + limits.sigma)), height: round(y(limits.mean - limits.sigma) - y(limits.mean + limits.sigma)) }
      : undefined,
    ucl: round(y(limits.ucl)),
    lcl: round(y(limits.lcl)),
    mean: round(y(limits.mean)),
    path: model.points.map((point, index) => `${round(x(index))},${round(y(point.value))}`).join(' '),
    flagged,
    last: flagged.length === 0 ? { x: round(x(n - 1)), y: round(y(lastPoint.value)) } : undefined,
    shade,
  };
}
